import React from 'react';
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubjectAccessCheckout } from '../hooks/usePayments';
import { SubjectPaymentCheckoutStatus } from '../types/payments';
import { useAuthStore } from '../stores/auth';
import { UserRole } from '../types/roles';

const PaynowReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const checkoutReference =
    searchParams.get('checkout_reference') || searchParams.get('reference') || '';
  const { data: checkout, isLoading, isFetching } = useSubjectAccessCheckout(
    checkoutReference,
    Boolean(checkoutReference)
  );

  const returnPath = user?.role === UserRole.ADMIN ? '/admin' : '/chat';

  const renderBody = () => {
    if (!checkoutReference) {
      return (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-6 text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-rose-300" />
          <h2 className="mt-4 text-xl font-semibold text-white">Missing checkout reference</h2>
          <p className="mt-2 text-sm text-slate-300">
            We could not find the payment reference needed to confirm your Paynow transaction.
          </p>
        </div>
      );
    }

    if (isLoading || isFetching || !checkout) {
      return (
        <div className="rounded-2xl border border-cyan-500/20 bg-slate-900/60 p-6 text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyan-300" />
          <h2 className="mt-4 text-xl font-semibold text-white">Confirming your payment</h2>
          <p className="mt-2 text-sm text-slate-300">
            We&apos;re checking Paynow and your subject access status now.
          </p>
        </div>
      );
    }

    if (checkout.status === SubjectPaymentCheckoutStatus.COMPLETED) {
      return (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
          <h2 className="mt-4 text-xl font-semibold text-white">Payment confirmed</h2>
          <p className="mt-2 text-sm text-slate-300">
            Your subject access is active and ready to use.
          </p>
        </div>
      );
    }

    if (
      checkout.status === SubjectPaymentCheckoutStatus.AWAITING_PAYMENT ||
      checkout.status === SubjectPaymentCheckoutStatus.PROCESSING
    ) {
      return (
        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/10 p-6 text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyan-300" />
          <h2 className="mt-4 text-xl font-semibold text-white">Payment still processing</h2>
          <p className="mt-2 text-sm text-slate-300">
            {checkout.feedback_reason ||
              'We have your checkout and are still waiting for provider confirmation.'}
          </p>
        </div>
      );
    }

    return (
      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-6 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-300" />
        <h2 className="mt-4 text-xl font-semibold text-white">Payment needs attention</h2>
        <p className="mt-2 text-sm text-slate-300">
          {checkout.feedback_reason ||
            'The Paynow checkout did not complete successfully. You can return to the app and try again.'}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 py-12 text-white">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <p className="text-sm uppercase tracking-[0.25em] text-cyan-300">Shasha Payments</p>
          <h1 className="mt-3 text-3xl font-bold">Paynow return</h1>
          <p className="mt-3 text-sm text-slate-300">
            Checkout reference: <span className="font-mono">{checkoutReference || 'Unavailable'}</span>
          </p>
        </div>

        {renderBody()}

        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={() => navigate(returnPath)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Return to app
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => window.location.reload()}
            className="text-sm font-medium text-slate-300 underline underline-offset-4"
          >
            Refresh status
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaynowReturn;
